import { IQService, IPromise } from "angular";
import { IModalService, IModalScope } from "angular-ui-bootstrap";
import { encode as base64encode } from "js-base64";
import {
  IInsight,
  IInsightQueryLayout,
  IAggregationField,
  IInsightQueryCondition,
  IInsightSqlConditionOperators,
  IInsightCalculationPayload,
  IConnectionData,
  IInsightCalculationTransaction,
  ISqlCondition,
  IDataSourceEntity,
  IDataSourceEntitiesMap,
  ConditionalOperator,
  SqlFunction,
  SupportedOperators,
  SupportedDataType,
} from "../../../models/models";
import { IDataflowBlueprintItem, IDataflowConnection, IGetDataFlowParams } from "@gtmhub/datasources/models/models";
import { ICollection } from "@gtmhub/models";
import { IField, IEntity } from "@gtmhub/entities/models/models";
import { DataSourceService } from "@gtmhub/datasources/services/dataSourceService";
import { EntityService } from "@gtmhub/entities/services/EntityService";
import { InsightService } from "../../../services/insightService";
import { localize } from "@gtmhub/localization";
import { QueryBuilderService } from "@gtmhub/gtmhub/services/queryBuilderService";
import { IDynamicItemSettings, IBuildInsightContext, IUpdateInsightSelectorDataActionParams, IBrowseInsightsActionParams } from "../models";
import { UIErrorHandlingService, IUIError } from "@gtmhub/error-handling";
import { byTextField } from "@gtmhub/util/sorting";
import { isNumberRounded, toFixedWithoutRounding } from "@gtmhub/shared/utils";
import { IMetric } from "@gtmhub/okrs/metrics";
import { isInsightCodeless } from "../../../util";
import { IConnection } from "@gtmhub/connectors/models";

interface IDataflowAndSqlOperatorsMap {
  dataFlow: IDataflowBlueprintItem[];
  sqlOperators: ICollection<IInsightSqlConditionOperators>;
}

export interface IBuildInsightScope extends IModalScope {
  insightTitle: string;
  canGoBack: boolean;
  confirming: boolean;
  aggregationFieldTitle: string;
  calculatedInsightValue: number;
  conditionEntityFields: IField[];
  connectionData: IConnectionData;
  dataSourceEntities: IDataSourceEntity[];
  dataSourceConnections: IConnection[];
  connectionName: string;
  entityFields: IField[];
  // dropdown directive focused elements for applying styling
  focusedEntity: IDataSourceEntity;
  focusedEntityField: IField;
  focusedAggregationField: IAggregationField;
  usageInMetrics: IMetric[];
  duplicate: {
    duplicateInsight: boolean;
    insightTitle: string;
  };
  indicators: {
    executing: boolean;
    confirming: boolean;
    loading: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/naming-convention
  SQLConditions: ISqlCondition[];
  sqlOperator: ConditionalOperator;
  sqlQuery: IInsightQueryLayout;
  addCondition(): void;
  clearAggregationField(): void;
  clearConditionName(condition: IInsightQueryCondition, index: number): void;
  clearConditionValue(condition: IInsightQueryCondition, index: number): void;
  clearDataSource(): void;
  clearDataSourceEntity(): void;
  confirm(): void;
  execute(): void;
  goBack(): void;
  resetCalculation(): void;
  // dropdown-keys directive callbacks
  // cb args and focused element can be literally anything
  onAgregationFieldSelected(focusedElement: IAggregationField, keyReleased: boolean): void;
  onEntitySelected(focusedElement: IDataSourceEntity, keyReleased: boolean): void;
  onEntityFieldSelected(focusedElement: IField, keyReleased: boolean, cbArgs: IInsightQueryCondition): void;
  removeCondition(index: number): void;
  setAggregationField(field: IField): void;
  setConditionValue(condition: IInsightQueryCondition, conditionValue: string): void;
  setDataSource(dataSource: IDataSourceEntity): void;
  setDatasourceEntityFromList(): void;
  setEntity(entity: IEntity): void;
  setEntityField(field: IField, condition: IInsightQueryCondition): void;
  setEntityFieldDataFromList(condition: IInsightQueryCondition): void;
  setSqlFunction(statement: string): void;
  setSQLOperator(sqlOperator: string): void;
}

interface ISelectFromListScope extends IModalScope {
  applySelection(): void;
  searchFieldData(search: string): void;
  selectedItems: string[];
  selectItem(value: string): void;
  fieldQuerySearch: boolean;
  items: IDataSourceEntitiesMap | { value: string }[];
  setDataSource(bluePrintConnection: IDataSourceEntity): void;
  onDatasourceSelected(entity: IDataSourceEntity): void;
  selectDatasourceEntity: boolean;
  confirm(): void;
  close(): void;
  indicators: { loading?: boolean; loadingFieldData?: boolean; saving?: boolean };
}

export class BuildInsightCtrl {
  public static $inject = [
    "$uibModal",
    "$q",
    "$scope",
    "buildInsightContext",
    "DataSourceService",
    "EntityService",
    "InsightService",
    "QueryBuilderService",
    "UIErrorHandlingService",
  ];

  get connectionName(): string {
    return this.buildInsightContext && this.buildInsightContext.connectionData ? this.buildInsightContext.connectionData.name : null;
  }

  constructor(
    private $uibModal: IModalService,
    private $q: IQService,
    private $scope: IBuildInsightScope,
    private buildInsightContext: IBuildInsightContext,
    private dataSourceService: DataSourceService,
    private entityService: EntityService,
    private insightService: InsightService,
    private queryBuilder: QueryBuilderService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.addCondition = this.addCondition;
    this.$scope.clearDataSource = this.clearDataSource;
    this.$scope.clearAggregationField = this.clearAggregationField;
    this.$scope.clearConditionName = this.clearConditionName;
    this.$scope.clearConditionValue = this.clearConditionValue;
    this.$scope.clearDataSourceEntity = this.clearDataSourceEntity;
    this.$scope.resetCalculation = this.resetCalculation;
    this.$scope.confirm = this.confirm;
    this.$scope.execute = this.execute;
    this.$scope.goBack = this.goBack;
    this.$scope.removeCondition = this.removeCondition;
    this.$scope.setAggregationField = this.setAggregationField;
    this.$scope.setConditionValue = this.setConditionValue;
    this.$scope.setDataSource = this.setDataSource;
    this.$scope.setDatasourceEntityFromList = this.setDatasourceEntityFromList;
    this.$scope.setDataSource = this.setDataSource;
    this.$scope.setEntity = this.setEntity;
    this.$scope.setEntityField = this.setEntityField;
    this.$scope.setEntityFieldDataFromList = this.setEntityFieldDataFromList;
    this.$scope.setSqlFunction = this.setSqlFunction;
    this.$scope.setSQLOperator = this.setSQLOperator;

    this.$scope.indicators = {
      executing: false,
      confirming: false,
      loading: true,
    };

    this.$scope.canGoBack = buildInsightContext.canGoBack;
    this.$scope.sqlOperator = "AND"; // default
    this.$scope.SQLConditions = [];
    this.$scope.duplicate = {
      duplicateInsight: false,
      insightTitle: "",
    };

    // dropdown keys directive callback functions
    this.$scope.onAgregationFieldSelected = this.onAgregationFieldSelected;
    this.$scope.onEntityFieldSelected = this.onEntityFieldSelected;
    this.$scope.onEntitySelected = this.onEntitySelected;

    // insight calculation response
    this.$scope.$on("insightResponse", this.onInsightResponse);

    this.init();
  }

  private insightCalculationPromise: IPromise<IInsightCalculationTransaction>;
  private getDataflowFields = "blueprint{id,name,title,logoUrl},connections{connection{name},dataSources{dataSource{name},entities{name,title}}}";

  private fieldName: string;

  private sqlOperators = [];

  private insight: IInsight;

  private confirmedInsight: IInsight;

  private init = (): void => {
    this.$scope.connectionData = this.buildInsightContext.connectionData;

    this.loadInsightFromParentScope();
    const dataSourceBlueprintNames = this.buildInsightContext.connectionData ? [this.buildInsightContext.connectionData.name] : null;
    const dataFlowRequest: IGetDataFlowParams = {
      dataSourceBlueprintNames,
      fields: this.getDataflowFields,
      includeEntities: true,
      includeStatuses: false,
    };
    this.$q
      .all({
        dataFlow: this.dataSourceService.getDataflow(dataFlowRequest),
        sqlOperators: this.insightService.getInsightSqlConditionOperators(),
      })
      .then(
        (map: IDataflowAndSqlOperatorsMap) => {
          this.$scope.indicators.loading = false;
          this.prepareInsightBuilder(map);
        },
        (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading" })
      );
  };

  private addCondition = (): void => {
    // pushing empty condition, its data and values are managed through the view using ng-model
    this.$scope.SQLConditions.push({
      strOperator: SupportedOperators.Equals, // by default
      value: null,
      dataType: null,
      title: null,
      field: null,
      sqlOperators: [],
    });
    this.resetCalculation();
  };

  private clearAggregationField = (): void => {
    this.$scope.sqlQuery.aggregatedField = {
      name: null,
      title: null,
      type: null,
    };

    setTimeout(() => document.getElementById("aggregatedField").focus());
    this.resetCalculation();
  };

  private clearConditionName = (condition: IInsightQueryCondition, index: number): void => {
    condition.field = null;
    condition.title = null;

    setTimeout(() => document.getElementById(`conditionId${index}`).focus());
    this.resetCalculation();
  };

  private clearConditionValue = (condition: IInsightQueryCondition, index: number): void => {
    condition.value = null;

    setTimeout(() => document.getElementById(`conditionValueId${index}`).focus());
    this.resetCalculation();
  };

  private clearDataSource = (): void => {
    this.$scope.sqlQuery.dataSource = null;
    this.$scope.sqlQuery.dataSourceImg = null;
    this.$scope.sqlQuery.entity = null;
    this.$scope.sqlQuery.entityTitle = null;
    this.$scope.sqlQuery.aggregatedField = null;

    setTimeout(() => document.getElementById("setDatasource").focus());
    this.resetCalculation();
  };

  private clearDataSourceEntity = (): void => {
    this.$scope.sqlQuery.entity = null;
    this.$scope.sqlQuery.aggregatedField = null;
    this.$scope.sqlQuery.entityTitle = null;
    this.$scope.entityFields = null;
    this.$scope.aggregationFieldTitle = null;
    this.$scope.SQLConditions = [];
    this.$scope.sqlQuery.conditions = null;

    this.clearDataSource();
    this.resetCalculation();
  };

  private createInsight = (isCloned: boolean): void => {
    this.insightService.createInsight(this.insight, { cloned: isCloned }).then(
      (createdInsight: IInsight) => {
        this.$scope.confirming = false;

        const params: IUpdateInsightSelectorDataActionParams = {
          action: "updateInsightSelectorData",
          settings: this.getFilterSettings(),
          // connection data is not persisted in data model => reassign to insight in insight selector
          insight: { ...createdInsight, connection: this.buildInsightContext.connectionData },
          calculatedValue: this.$scope.calculatedInsightValue,
          fieldName: this.fieldName,
        };

        this.$scope.$close(params);
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "confirming", inline: true })
    );
  };

  private confirm = (): void => {
    this.setInsightProperties();

    if (!this.confirmedInsight) {
      this.createInsight(false);
    } else {
      if (this.$scope.duplicate.duplicateInsight) {
        this.insight.title = this.$scope.duplicate.insightTitle ? this.$scope.duplicate.insightTitle : this.insight.title + " Cloning";
        this.insight.name = this.generateInsightName();
        this.createInsight(true);
      } else {
        this.updateInsight();
      }
    }
  };

  private enrichDatasourceWithEntities = (dataFlows: IDataflowBlueprintItem[], enrichedItems: IDataSourceEntity[] = []): IDataSourceEntity[] => {
    dataFlows.forEach((dataFlow) => {
      dataFlow.connections.forEach((connection) => {
        connection.dataSources.forEach((dataSource) => {
          dataSource.entities.forEach((entity) => {
            enrichedItems.push({
              connectionData: {
                id: connection.connection.id,
                name: connection.connection.name,
                title: dataFlow.blueprint.title + " -> " + connection.connection.name,
              },
              dataSource: dataFlow.blueprint.name, // actual data source name,
              dataSourceImg: dataFlow.blueprint.name, // necessary for matching the img href
              dataSourceLogoUrl: dataFlow.blueprint.logoUrl,
              dataSourceTitle: dataFlow.blueprint.title, // beautified title
              aggregatedTitle: dataFlow.blueprint.title + " -> " + entity.name, // title will be used to display beautified MySQL -> deals and so on,
              entity: entity.name, // used for selecting an entity to operate with
              entityTitle: entity.title, // displaying beautified entity name
            });
          });
        });
      });
    });

    return enrichedItems;
  };

  private getEncodedScript = (): string => {
    const script = this.queryBuilder.generateSqlScript(this.$scope.SQLConditions, this.$scope.sqlOperator, this.$scope.sqlQuery);
    return base64encode(script || "");
  };

  private execute = (): void => {
    const payload: IInsightCalculationPayload = {
      language: "sql",
      script: this.getEncodedScript(),
      filters: {},
      settings: [],
      id: null,
    };

    this.$scope.indicators.executing = true;
    this.insightCalculationPromise = this.insightService.requestInsightScriptCalculation(payload);
  };

  private fillInsightQueryLayout = (insight: IInsight): void => {
    const queryLayout = insight.queryLayout;

    const bluePrintConnection = this.$scope.dataSourceEntities.reduce((aggregator, dataSourceEntity) => {
      if (queryLayout.entity === dataSourceEntity.entity && queryLayout.entityTitle === dataSourceEntity.entityTitle) {
        aggregator = dataSourceEntity;
      }
      return aggregator;
    }, null);

    if (bluePrintConnection) {
      this.setDataSource(bluePrintConnection);
    }

    if (queryLayout.aggregatedField) {
      this.setAggregationField(queryLayout.aggregatedField);
    }

    if (queryLayout.conditions) {
      this.$scope.SQLConditions = this.queryBuilder.flattenConditions(queryLayout.conditions, this.sqlOperators);

      if (queryLayout.conditions.nextConditionBooleanLogic) {
        this.$scope.sqlOperator = queryLayout.conditions.nextConditionBooleanLogic;
      }
    }
  };

  private generateInsight = (): IInsight => {
    return {
      id: "",
      processing: false,
      accountId: "",
      name: this.generateInsightName(),
      title: this.getInsightTitle(),
      description: "",
      categoryId: "",
      requiredEntities: null,
      status: "",
      direct: false,
      formatting: {
        isCodeless: true,
        editor: "automatic",
      },
      view: "",
      language: "sql",
      isScalar: true,
      algorithm: this.queryBuilder.generateSqlScript(this.$scope.SQLConditions, this.$scope.sqlOperator, this.$scope.sqlQuery),
      isShadow: true,
    };
  };

  private generateInsightName = (): string => {
    return Math.random().toString(36).substr(2, 10);
  };

  private getEntityData = (fields: string, search?: string): void => {
    this.entityService.requestEntityData(this.$scope.sqlQuery.entity, { search, skip: 0, take: 100, fields, distinct: true });
  };

  private getEntityFields = (entityName: string): void => {
    this.entityService.getEntity(entityName).then(
      (entity) => {
        if (entity.fields) {
          this.$scope.entityFields = entity.fields.filter((entityEl) => this.queryBuilder.isNumber(entityEl.type));

          this.$scope.conditionEntityFields = entity.fields;
        }
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private getFilterSettings = (): IDynamicItemSettings[] => {
    const parameters: IDynamicItemSettings[] = [];

    this.$scope.SQLConditions.forEach((sqlCondition) => {
      const parameter: IDynamicItemSettings = {
        name: sqlCondition.field,
        title: sqlCondition.title,
        value: sqlCondition.value !== null && sqlCondition.value !== undefined ? sqlCondition.value.toString() : sqlCondition.value,
        valueDisplayName: null,
        type: "text",
      };

      parameters.push(parameter);
    });

    // sort in alphabetic order
    return parameters.sort(byTextField("title"));
  };

  private getInsightTitle = (): string => {
    const funcMap = {
      sum: "total_sum_of",
      avg: "average_of",
      min: "min_of",
      max: "max_of",
      count: "total_count_of",
    };

    const sqlQuery = this.$scope.sqlQuery;
    const sqlFunc = sqlQuery.sqlFunction ? localize(funcMap[sqlQuery.sqlFunction]) : null;
    const entityTitle = sqlQuery.entityTitle;
    const aggrField = sqlQuery.aggregatedField ? sqlQuery.aggregatedField.title : null;

    if (!entityTitle || !sqlFunc) {
      return "";
    } else if (!aggrField) {
      return `${sqlFunc}: ${entityTitle}`;
    } else {
      return `${sqlFunc}: ${entityTitle} -> ${aggrField}`;
    }
  };

  private goBack = (): void => {
    const params: IBrowseInsightsActionParams = {
      action: "browseInsights",
      connectionData: this.buildInsightContext.connectionData,
    };

    this.$scope.$close(params);
  };

  private resetCalculation = (): void => {
    this.$scope.calculatedInsightValue = undefined;
    this.$scope.indicators.executing = false;
    this.insightCalculationPromise = null;
  };

  private loadInsightFromParentScope = (): void => {
    // only load existing insight if it is from codeless type;
    // otherwise, user can ruin a standard insight by unintentionally editing its sql since it won't be loaded in insight builder
    const confirmedInsightIsCodeless = this.buildInsightContext.confirmedInsight && isInsightCodeless(this.buildInsightContext.confirmedInsight);
    if (confirmedInsightIsCodeless) {
      this.confirmedInsight = this.buildInsightContext.confirmedInsight;
      this.insight = angular.copy(this.buildInsightContext.confirmedInsight);
      this.$scope.insightTitle = this.insight.title;
    }
  };

  private onAgregationFieldSelected = (focusedElement: IAggregationField, keyReleased: boolean): void => {
    this.$scope.focusedAggregationField = focusedElement;

    if (keyReleased) {
      this.setAggregationField(this.$scope.focusedAggregationField);
    }
  };

  private onEntityFieldSelected = (focusedElement: IField, keyReleased: boolean, cbConditionArg: IInsightQueryCondition): void => {
    this.$scope.focusedEntityField = focusedElement;

    if (keyReleased) {
      this.setEntityField(this.$scope.focusedEntityField, cbConditionArg);
    }
  };

  private onEntitySelected = (focusedElement: IDataSourceEntity, keyReleased: boolean): void => {
    this.$scope.focusedEntity = focusedElement;

    if (keyReleased) {
      this.setDataSource(this.$scope.focusedEntity);
    }
  };

  private onInsightResponse = (ev, response): void => {
    if (!this.insightCalculationPromise) {
      return;
    }

    this.insightCalculationPromise.then(
      (transaction) => {
        if (transaction.transactionId === response.transactionID) {
          if (response.data.status === "error") {
            const error: IUIError = {
              body: response.message,
            };

            this.uiErrorHandlingService.handleIndicator(error, { indicator: "executing", inline: true });
          } else {
            this.$scope.indicators.executing = false;

            const [fieldName, number] = Object.entries(response.data.data)[0] as [string, number];
            this.$scope.calculatedInsightValue = number;
            this.fieldName = fieldName;

            const isRounded = isNumberRounded(number);

            if (!isRounded) {
              this.$scope.calculatedInsightValue = toFixedWithoutRounding(number);
            }
          }
        }
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "executing" })
    );
  };

  private prepareInsightBuilder = (map: IDataflowAndSqlOperatorsMap): void => {
    const insightExists = !!this.insight;
    const insightHasLayout: boolean = insightExists ? this.insight.queryLayout !== null : false;

    this.sqlOperators = map.sqlOperators.items;

    this.setDataSourceEntities(map.dataFlow);

    if (insightExists && insightHasLayout) {
      const confirmedInsightConnectionName = this.getInsightConnectionName(this.confirmedInsight);
      const currentConnectionName = this.buildInsightContext.connectionData.name;

      // prevents displaying codeless insight in insight builder if connection saved in confirmed insight (already linked to metric insight) and user-selected connection differ
      if (confirmedInsightConnectionName === currentConnectionName) {
        this.setCustomQuery();
      } else {
        this.setDefaultQuery();
        // update insight's connection data => needed for proper navigation between the screens of the insight selector
        this.insight.connection = this.buildInsightContext.connectionData;
      }
    } else if (insightExists && !insightHasLayout) {
      this.setDefaultQuery();
    } else {
      this.setDefaultQuery();
      this.insight = this.generateInsight();
    }
    this.getInsightUsageInMetrics();
  };

  private removeCondition = (index: number): void => {
    this.$scope.SQLConditions = this.$scope.SQLConditions.filter((condition, idx) => idx !== index);

    if (this.$scope.SQLConditions.length === 0) {
      this.$scope.sqlQuery.conditions = null;
    }
    this.resetCalculation();
  };

  private setAggregationField = (field: IAggregationField): void => {
    this.$scope.sqlQuery.aggregatedField = {
      name: field.name,
      title: field.title,
      type: field.type,
    };
    this.resetCalculation();
  };

  private setConditionValue = (condition: IInsightQueryCondition, conditionValue: string): void => {
    condition.value = conditionValue;
    this.resetCalculation();
  };

  private setCustomQuery = (): void => {
    this.$scope.sqlQuery = this.insight.queryLayout;
    this.fillInsightQueryLayout(this.insight);
  };

  private getInsightConnectionName = (insight: IInsight): string => {
    return insight && insight.connection ? insight.connection.name : null;
  };

  private setDataSource = (bluePrintConnection: IDataSourceEntity): void => {
    this.$scope.sqlQuery.dataSource = bluePrintConnection.dataSource;
    this.$scope.sqlQuery.dataSourceImg = bluePrintConnection.dataSourceImg;
    this.$scope.sqlQuery.dataSourceLogoUrl = bluePrintConnection.dataSourceLogoUrl;
    this.$scope.sqlQuery.entity = bluePrintConnection.entity;
    this.$scope.sqlQuery.entityTitle = bluePrintConnection.entityTitle;

    this.$scope.SQLConditions = []; // changing datasource have to reset all existing conditions

    this.resetCalculation();
    this.getEntityFields(bluePrintConnection.entity);
  };

  private setDataSourceEntities = (dataFlow: IDataflowBlueprintItem[]): void => {
    if (this.connectionName) {
      const { connectionDataFlow, connections } = dataFlow.reduce(
        (aggregator: { connectionDataFlow: IDataflowBlueprintItem[]; connections: IConnection[] }, item: IDataflowBlueprintItem) => {
          if (item.blueprint.name !== this.connectionName) {
            return aggregator;
          }

          aggregator.connectionDataFlow.push(item);
          aggregator.connections = item.connections.reduce((connectionsAggregator: IConnection[], con: IDataflowConnection) => {
            if (!connectionsAggregator.some((c) => c.id === con.connection.id)) {
              connectionsAggregator.push(con.connection);
            }
            return connectionsAggregator;
          }, aggregator.connections);

          return aggregator;
        },
        { connectionDataFlow: <IDataflowBlueprintItem[]>[], connections: <IConnection[]>[] }
      );
      this.$scope.dataSourceEntities = this.enrichDatasourceWithEntities(connectionDataFlow);
      this.$scope.dataSourceConnections = connections;
      this.$scope.connectionName = this.connectionName;
    } else {
      this.$scope.dataSourceEntities = this.enrichDatasourceWithEntities(dataFlow);
    }
  };

  private setDatasourceEntityFromList = (): void => {
    this.resetCalculation();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../../views/select-from-list.jade"),
      windowClass: "modal-small",
      controller: [
        "$scope",
        function ($scope: ISelectFromListScope) {
          const setDataSourceEntitiesMap = (): IDataSourceEntitiesMap => {
            return ctrl.$scope.dataSourceEntities.reduce((aggregator: IDataSourceEntitiesMap, entity: IDataSourceEntity) => {
              if (!aggregator[entity.dataSource]) {
                aggregator[entity.dataSource] = {
                  entities: [],
                  connections: [],
                  dataSource: entity.dataSource,
                  dataSourceTitle: entity.dataSourceTitle,
                  dataSourceLogoUrl: entity.dataSourceLogoUrl,
                  showEntities: true,
                };
              }
              aggregator[entity.dataSource].entities.push(entity);
              if (entity.connectionData.id && !aggregator[entity.dataSource].connections.some((c) => c.id === entity.connectionData.id)) {
                aggregator[entity.dataSource].connections.push(entity.connectionData);
              }

              return aggregator;
            }, {});
          };

          $scope.indicators = {
            loading: false,
          };

          $scope.close = () => {
            $scope.$close();
          };

          $scope.confirm = () => {
            $scope.indicators = {
              saving: true,
            };
          };

          $scope.selectDatasourceEntity = true;
          $scope.setDataSource = ctrl.setDataSource;
          $scope.onDatasourceSelected = (entity: IDataSourceEntity) => {
            ctrl.setDataSource(entity);
            $scope.close();
          };

          $scope.items = setDataSourceEntitiesMap();
        },
      ],
    });
  };

  private setDefaultQuery = (): void => {
    this.$scope.sqlQuery = {
      sqlFunction: null,
      dataSource: null,
      dataSourceImg: null,
      dataSourceLogoUrl: null,
      entity: null,
      entityTitle: null,
      aggregatedField: null,
    };

    this.setSqlFunction(SqlFunction.Count); // def operator
  };

  private setEntity = (dataSource: IEntity): void => {
    this.$scope.sqlQuery.entity = dataSource.name;
    this.$scope.sqlQuery.entityTitle = dataSource.title;
    this.$scope.sqlQuery.aggregatedField = null;

    this.getEntityFields(dataSource.name);
    this.resetCalculation();
  };

  private setEntityField = (entityField: IField, condition: IInsightQueryCondition) => {
    condition.title = entityField.title;
    condition.dataType = entityField.type as SupportedDataType;
    condition.field = entityField.name;
    condition.value = null; // necessary for field transition from string to number to avoid type errors
    condition.sqlOperators = this.queryBuilder.getConditionSqlOperators(condition.dataType, this.sqlOperators);
  };

  private setEntityFieldDataFromList = (condition: IInsightQueryCondition): void => {
    this.resetCalculation();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../../views/select-from-list.jade"),
      windowClass: "modal-small",
      controller: [
        "$scope",
        function ($scope: ISelectFromListScope) {
          $scope.fieldQuerySearch = true;
          $scope.selectedItems = [];
          $scope.indicators = {
            loading: true,
            loadingFieldData: false,
          };

          $scope.$on("entityDataResponse", (e, data: { accountId: string; entityName: string; entity: string[] }): void => {
            $scope.items = data.entity.map((entity) => {
              return {
                value: entity[condition.field],
              };
            });

            $scope.indicators.loading = false;
            $scope.indicators.loadingFieldData = false;
          });

          // on initial load 100 items
          ctrl.getEntityData(condition.field);

          $scope.searchFieldData = (search: string): void => {
            $scope.indicators.loadingFieldData = true;
            ctrl.getEntityData(condition.field, search);
            search = "";
          };

          $scope.selectItem = (value: string): void => {
            if ($scope.selectedItems.indexOf(value) === -1) {
              $scope.selectedItems.push(value);
              return;
            }
            $scope.selectedItems = $scope.selectedItems.filter((element: string) => element !== value);
          };

          $scope.applySelection = (): void => {
            condition.isSingleValue = $scope.selectedItems.length === 1;
            condition.value = $scope.selectedItems.reduce((selection: string, val: string, i: number) => (i === 0 ? val : `${selection}, ${val}`), "");
          };

          $scope.close = () => {
            $scope.$close();
          };
        },
      ],
    });
  };

  private setInsightProperties = (): void => {
    // updates the cached insight with newest algorithm (sql)
    this.insight.algorithm = this.queryBuilder.generateSqlScript(this.$scope.SQLConditions, this.$scope.sqlOperator, this.$scope.sqlQuery);
    // if we are patching existing codeless insight we will update its name without condition
    if (!this.insight.title || this.insight.isShadow) {
      this.insight.title = this.getInsightTitle();
    }
    this.insight.queryLayout = this.$scope.sqlQuery;
  };

  private setSqlFunction = (sqlFunc: SqlFunction): void => {
    this.$scope.sqlQuery.sqlFunction = sqlFunc;
    this.resetCalculation();
  };

  private setSQLOperator = (operator: ConditionalOperator): void => {
    this.$scope.sqlOperator = operator;
    this.resetCalculation();
  };

  private updateInsight = (): void => {
    this.insightService.patchInsight(this.insight).then(
      () => {
        this.$scope.confirming = false;

        const params: IUpdateInsightSelectorDataActionParams = {
          action: "updateInsightSelectorData",
          settings: this.getFilterSettings(),
          insight: this.insight,
          calculatedValue: this.$scope.calculatedInsightValue,
          fieldName: this.fieldName,
        };

        this.$scope.$close(params);
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "confirming", inline: true })
    );
  };

  private getInsightUsageInMetrics = () => {
    if (this.insight.id) {
      this.insightService.getUsageInMetrics(this.insight.id).then((usage: IMetric[]) => {
        this.$scope.usageInMetrics = usage;
      });
    }
  };
}
